/**
 * SpotlightProvider — React context holding registered configs +
 * current spotlight id. Spec: docs/specs/cuidari/t10-onboarding-journey.md §9.2.
 *
 * Mounted once in `main.tsx`; descendant pages call `useSpotlight()` to
 * activate by id. Feature PRs bolt on real configs via the `configs`
 * prop or `useSpotlightFromUrl` (which calls `registerConfigs`).
 */
import { createContext, useState, type ReactNode } from 'react'

import type { SpotlightApi, SpotlightConfig } from './types'

interface SpotlightContextValue extends SpotlightApi {
  configs: Record<string, SpotlightConfig>
  registerConfigs: (extras: Record<string, SpotlightConfig>) => void
}

export const SpotlightContext = createContext<SpotlightContextValue | null>(null)

interface SpotlightProviderProps {
  configs?: Record<string, SpotlightConfig>
  children: ReactNode
}

export function SpotlightProvider({
  configs: initialConfigs = {},
  children,
}: SpotlightProviderProps) {
  const [currentId, setCurrentId] = useState<string | null>(null)
  // Seeded once; subsequent changes flow through `registerConfigs` so
  // page-local configs (from `useSpotlightFromUrl`) can merge in.
  const [configs, setConfigs] = useState<Record<string, SpotlightConfig>>(
    () => ({ ...initialConfigs }),
  )

  const activate = (id: string) => setCurrentId(id)
  const deactivate = () => setCurrentId(null)
  const registerConfigs = (extras: Record<string, SpotlightConfig>) =>
    setConfigs((prev) => ({ ...prev, ...extras }))

  const current = currentId != null ? configs[currentId] ?? null : null
  const value: SpotlightContextValue = {
    configs,
    current,
    activate,
    deactivate,
    registerConfigs,
  }

  return <SpotlightContext.Provider value={value}>{children}</SpotlightContext.Provider>
}
